import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import api, { errMsg } from "../api";
import { toast } from "sonner";
import { Loader2, Repeat, Edit3, Trash2, Check, X } from "lucide-react";

function formatLocal(iso) {
    // Convert ISO to value usable by datetime-local input
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function toIso(local) {
    if (!local) return null;
    const d = new Date(local);
    return d.toISOString();
}

/** Master/Admin can edit an existing on-call entry. */
export function OnCallEditDialog({ oncall, users, onClose, onChanged, lang }) {
    const [userId, setUserId] = useState(oncall?.user_id || "");
    const [start, setStart] = useState(oncall ? formatLocal(oncall.start) : "");
    const [end, setEnd] = useState(oncall ? formatLocal(oncall.end) : "");
    const [note, setNote] = useState(oncall?.note || "");
    const [saving, setSaving] = useState(false);
    if (!oncall) return null;
    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/on-call/${oncall.id}`, {
                user_id: userId, start: toIso(start), end: toIso(end), note,
            });
            toast.success(lang === "de" ? "Bereitschaft aktualisiert" : "On-call updated");
            onChanged?.();
            onClose();
        } catch (e) {
            toast.error(errMsg(e, "Fehler"));
        } finally { setSaving(false); }
    };
    const remove = async () => {
        if (!window.confirm(lang === "de" ? "Bereitschaft wirklich löschen?" : "Delete on-call entry?")) return;
        setSaving(true);
        try {
            await api.delete(`/on-call/${oncall.id}`);
            toast.success(lang === "de" ? "Gelöscht" : "Deleted");
            onChanged?.();
            onClose();
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setSaving(false); }
    };
    return (
        <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="oncall-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Edit3 className="w-4 h-4" /> {lang === "de" ? "Bereitschaft bearbeiten" : "Edit on-call"}</DialogTitle>
                    <DialogDescription>{lang === "de" ? "Master/Admin: Person, Zeitraum oder Notiz anpassen." : "Master/admin only."}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{lang === "de" ? "Mitarbeiter" : "User"}</Label>
                        <select value={userId} onChange={(e) => setUserId(e.target.value)} className="mt-1 w-full h-10 px-3 border border-neutral-300 rounded-md text-sm" data-testid="oncall-edit-user">
                            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label className="text-xs">Start</Label>
                            <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="oncall-edit-start" />
                        </div>
                        <div>
                            <Label className="text-xs">Ende</Label>
                            <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="oncall-edit-end" />
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs">{lang === "de" ? "Notiz" : "Note"}</Label>
                        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="oncall-edit-note" />
                    </div>
                </div>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={remove} disabled={saving} className="text-red-600 hover:text-red-700" data-testid="oncall-edit-delete">
                        <Trash2 className="w-3.5 h-3.5 mr-1" /> {lang === "de" ? "Löschen" : "Delete"}
                    </Button>
                    <div className="flex-1" />
                    <Button variant="outline" onClick={onClose} disabled={saving}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={save} disabled={saving} data-testid="oncall-edit-save">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                        {lang === "de" ? "Speichern" : "Save"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** The current on-call user requests swap from one or more colleagues. */
export function OnCallSwapDialog({ oncall, users, currentUserId, onClose, onSubmitted, lang }) {
    const [targets, setTargets] = useState([]);
    const [partial, setPartial] = useState(false);
    const [start, setStart] = useState(oncall ? formatLocal(oncall.start) : "");
    const [end, setEnd] = useState(oncall ? formatLocal(oncall.end) : "");
    const [reason, setReason] = useState("");
    const [sending, setSending] = useState(false);
    if (!oncall) return null;
    const candidates = users.filter((u) => u.id !== currentUserId);
    const toggle = (id) => setTargets((t) => t.includes(id) ? t.filter((x) => x !== id) : [...t, id]);
    const submit = async () => {
        if (targets.length === 0) { toast.error(lang === "de" ? "Mindestens eine Person wählen" : "Pick at least one person"); return; }
        setSending(true);
        try {
            await api.post(`/on-call/${oncall.id}/swap-request`, {
                target_user_ids: targets,
                start: partial ? toIso(start) : null,
                end: partial ? toIso(end) : null,
                reason,
            });
            toast.success(lang === "de" ? `Anfrage an ${targets.length} Person(en) gesendet` : `Sent to ${targets.length} person(s)`);
            onSubmitted?.();
            onClose();
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setSending(false); }
    };
    return (
        <Dialog open={true} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md" data-testid="oncall-swap-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Repeat className="w-4 h-4" /> {lang === "de" ? "Bereitschaft tauschen" : "Swap on-call"}</DialogTitle>
                    <DialogDescription>
                        {lang === "de" ? "Frag Kolleg:innen, ob jemand übernimmt. Wer zuerst zusagt, übernimmt." : "Ask colleagues; first to accept takes over."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs">{lang === "de" ? "An wen?" : "Recipients"}</Label>
                        <div className="mt-1 max-h-44 overflow-y-auto border border-neutral-200 rounded-md p-2 space-y-1" data-testid="oncall-swap-targets">
                            {candidates.map((u) => (
                                <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-0.5 rounded hover:bg-neutral-50">
                                    <input type="checkbox" checked={targets.includes(u.id)} onChange={() => toggle(u.id)} className="accent-brand-500" data-testid={`oncall-swap-target-${u.id}`} />
                                    <span className="flex-1 truncate">{u.name}</span>
                                    <span className="text-[10px] text-neutral-400 truncate">{u.email}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} className="accent-brand-500" data-testid="oncall-swap-partial" />
                        {lang === "de" ? "Nur einen Teil der Zeit tauschen" : "Swap only part of the time"}
                    </label>
                    {partial && (
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <Label className="text-xs">Start</Label>
                                <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid="oncall-swap-start" />
                            </div>
                            <div>
                                <Label className="text-xs">Ende</Label>
                                <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="oncall-swap-end" />
                            </div>
                        </div>
                    )}
                    <div>
                        <Label className="text-xs">{lang === "de" ? "Grund (optional)" : "Reason (optional)"}</Label>
                        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder={lang === "de" ? "z. B. Arzttermin" : "e.g. doctor's appointment"} data-testid="oncall-swap-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={sending}>{lang === "de" ? "Abbrechen" : "Cancel"}</Button>
                    <Button onClick={submit} disabled={sending || targets.length === 0} data-testid="oncall-swap-submit" className="bg-brand-600 hover:bg-brand-700 text-white">
                        {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Repeat className="w-4 h-4 mr-1" />}
                        {lang === "de" ? "Anfrage senden" : "Send request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

/** Inbox-Popover next to the Filter button — lists incoming swap requests with Accept/Reject. */
export function OnCallSwapInbox({ currentUserId, lang, onChanged }) {
    const [swaps, setSwaps] = useState([]);
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const load = async () => {
        try {
            const { data } = await api.get("/on-call/swap-requests");
            setSwaps(Array.isArray(data) ? data : []);
        } catch { setSwaps([]); }
    };
    useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, []);
    const incoming = swaps.filter((s) => s.status === "pending" && (s.target_user_ids || []).includes(currentUserId));
    const outgoing = swaps.filter((s) => s.requester_id === currentUserId);

    const act = async (swap, action) => {
        setBusy(swap.id);
        try {
            await api.post(`/on-call/swap-requests/${swap.id}/${action}`);
            await load();
            onChanged?.();
        } catch (e) { toast.error(errMsg(e, "Fehler")); }
        finally { setBusy(null); }
    };

    return (
        <div className="relative">
            <Button variant="outline" onClick={() => setOpen((o) => !o)} className="gap-1.5 relative" data-testid="oncall-inbox-btn">
                <Repeat className="w-3.5 h-3.5" />
                {lang === "de" ? "Tausch" : "Swaps"}
                {incoming.length > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] rounded-full bg-red-500 text-white font-semibold">
                        {incoming.length}
                    </span>
                )}
            </Button>
            {open && (
                <div className="absolute right-0 mt-1 w-96 max-h-[70vh] overflow-y-auto bg-white border border-neutral-200 rounded-md shadow-xl z-30 p-3" data-testid="oncall-inbox-popover">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2">
                        {lang === "de" ? "Eingehende Anfragen" : "Incoming"} · {incoming.length}
                    </p>
                    {incoming.length === 0 && <p className="text-xs text-neutral-500 mb-3">{lang === "de" ? "Keine offenen Anfragen." : "No open requests."}</p>}
                    <div className="space-y-2 mb-3">
                        {incoming.map((s) => (
                            <div key={s.id} className="border border-neutral-200 rounded-md p-2 bg-red-50/30" data-testid={`oncall-swap-row-${s.id}`}>
                                <p className="text-sm font-medium">{s.requester_name}</p>
                                <p className="text-[11px] text-neutral-600">{s.start} → {s.end}</p>
                                {s.reason && <p className="text-[11px] text-neutral-500 italic mt-0.5">&ldquo;{s.reason}&rdquo;</p>}
                                <div className="flex gap-1.5 mt-1.5">
                                    <Button size="sm" onClick={() => act(s, "accept")} disabled={busy === s.id} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7" data-testid={`oncall-swap-accept-${s.id}`}>
                                        <Check className="w-3 h-3 mr-1" /> {lang === "de" ? "Übernehmen" : "Accept"}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => act(s, "reject")} disabled={busy === s.id} className="h-7" data-testid={`oncall-swap-reject-${s.id}`}>
                                        <X className="w-3 h-3 mr-1" /> {lang === "de" ? "Ablehnen" : "Reject"}
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                    {outgoing.length > 0 && (
                        <>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-2 mt-2 border-t border-neutral-200 pt-2">
                                {lang === "de" ? "Eigene Anfragen" : "My requests"} · {outgoing.length}
                            </p>
                            <div className="space-y-1.5">
                                {outgoing.map((s) => (
                                    <div key={s.id} className="border border-neutral-200 rounded-md p-2 bg-white text-xs" data-testid={`oncall-swap-out-${s.id}`}>
                                        <div className="flex items-center justify-between">
                                            <p className="font-medium">{(s.target_user_ids || []).map((id) => s.target_user_names?.[id]).filter(Boolean).join(", ")}</p>
                                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                                                s.status === "accepted" ? "bg-emerald-100 text-emerald-700" :
                                                s.status === "rejected" ? "bg-red-100 text-red-700" :
                                                s.status === "cancelled" ? "bg-neutral-100 text-neutral-600" :
                                                "bg-amber-100 text-amber-700"
                                            }`}>
                                                {s.status === "accepted" ? (lang === "de" ? `✓ ${s.accepted_by_name}` : `✓ ${s.accepted_by_name}`) :
                                                 s.status === "pending" ? (lang === "de" ? "offen" : "pending") :
                                                 s.status}
                                            </span>
                                        </div>
                                        <p className="text-[10px] text-neutral-500">{s.start} → {s.end}</p>
                                        {s.status === "pending" && (
                                            <button onClick={() => act(s, "cancel")} className="mt-1 text-[10px] text-red-600 hover:text-red-700" data-testid={`oncall-swap-cancel-${s.id}`}>
                                                {lang === "de" ? "zurückziehen" : "cancel"}
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
